import { EciuImage, Rounded } from "@/components/EciuImage";
import { LearningOppHead } from "@/components/Head";
import ObjectHeader from "@/components/ObjectHeader";
import {
  LearningOpportunityCardContainerProps,
  LearningOpportunitySection,
} from "@/components/Sections";
import { AdmissionProgressSection } from "@/components/Sections/AdmissionProgressSection";
import { LearningOppAttendingMetaSection } from "@/components/Sections/LearningOppAttendingMetaSection";
import { LearningOppAttendingSection } from "@/components/Sections/LearningOppAttendingSection";
import { LearningOppMetaAndInfoColumnSection } from "@/components/Sections/LearningOppMetaAndInfoSection";
import { LearningOppProviderSection } from "@/components/Sections/LearningOppProviderSection";
import {
  LearningOpportunity,
  CompetenceBaseTreeEx,
  getCompetencePassportOrNull,
  getCompetenceTree,
  getLO,
  TeamMember,
} from "@/server/backend-api";
import {
  CompetencePassport,
  LearningOpportunityType,
  PrerequisiteResultWithEloId,
} from "@/server/backend-api/generated";
import {
  getAdmissionFiles,
  UserAdmissionFile,
} from "@/server/backend-api/learningOpportunityUserAdmission";
import {
  createAnchorLinksForPageVariant,
  getLoPageVariant,
  getSortedLearners,
  LoPageVersion,
} from "@/utils/loPageHelpers";
import type { GetServerSidePropsContext, NextPage } from "next";
import { withErrorReportingAndAuthorization } from "../../../middlewares/withErrorReportingAndAuthorisation";
import { getStatusLabelPropsFromStatus } from "@/components/StatusLabel/StatusLabel";
import { useSystemMessages } from "@/hooks/useSystemMessages";
import { useLogging } from "@/utils/appInsights/client";
import { useState } from "react";
import SpinnerOverlay from "@/components/SpinnerOverlay";
import {
  buildCertificateDownloadUrl,
  buildLearningOpportunityUrl,
  getRedirectIfUrlMismatch,
} from "@/utils/url";
import { AdmissionResponseSuccessDialog } from "@/components/Modal";
import {
  AdmissionResponseType,
  useAdmissionResponseSuccess,
} from "@/hooks/useAdmissionResponseSuccess";
import { joinLearningOppWithSimplifiedProcess } from "@/utils/fetch/apply";
import { LearningOpportunityApplyButton } from "@/components/LearningOpportunityApplyButton";
import { GetServerSidePropsResult } from "next/types";
import { AnchorLinkItem } from "@/components/AnchorLinks/AnchorLinks";
import { LearningOutcomeSections } from "@/components/Sections/LearningOutcomeSections";
import {
  getSSRMotivationScan,
  MotivationScanAnswers,
} from "@/utils/motivationScan";
import { getAuthStatus } from "@/server/getAuthStatus";
import {
  LEARNING_OPP_INFO_TEXT_TAB_SECTION_ID,
  LearningOppInformationTextTabSection,
} from "@/components/Sections/LearningOppInformationTextTabSection";
import { LearningOpportunityPageStaticData } from "@/_staticContent/learningOpportunityPage";
import { checkPrerequisites } from "@/server/backend-api/prerequisites";

const SECTIONS = {
  description: { id: "description", text: "Description" },
  information: {
    id: LEARNING_OPP_INFO_TEXT_TAB_SECTION_ID,
    text: "Information",
  },
  provider: { id: "providers", text: "Providers" },
  relatedLOs: { id: "related", text: "Related" },
  yourApplication: { id: "your-application", text: "Your application" },
  yourTeam: { id: "your-team", text: "Your team" },
  valueAndProgress: {
    id: "value-and-progress",
    text: "Value and progress",
  },
};

const LO_PAGE_SECTIONS_DEFAULT = [
  SECTIONS.description,
  SECTIONS.information,
  SECTIONS.valueAndProgress,
  SECTIONS.provider,
  SECTIONS.relatedLOs,
];
const LO_PAGE_SECTIONS_APPLIED = [
  SECTIONS.yourApplication,
  ...LO_PAGE_SECTIONS_DEFAULT,
];
const LO_PAGE_SECTIONS_ATTENDING = [
  SECTIONS.yourTeam,
  SECTIONS.valueAndProgress,
  SECTIONS.information,
  SECTIONS.description,
  SECTIONS.yourApplication,
  SECTIONS.provider,
  SECTIONS.relatedLOs,
];

const staticData = LearningOpportunityPageStaticData;

export type LearningOpportunityPageQueryParams = {
  showInitialHelpText?: "1";
};

type PageProps = {
  learningOpp: LearningOpportunity;
  userAdmissionFiles: UserAdmissionFile[];
  sortedLearners: TeamMember[];
  anchorLinks: AnchorLinkItem[];
  pageVariant: LoPageVersion;
  loListItems: LearningOpportunityCardContainerProps[];
  showInitialHelpText: boolean;
  tree: CompetenceBaseTreeEx;
  answers: MotivationScanAnswers | null;
  isSignedIn: boolean;
  competencePassport: CompetencePassport | null;
  prerequisites: PrerequisiteResultWithEloId[];
};

const getServerSidePropsImpl = async (
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<PageProps>> => {
  const id = context.params?.id as string;

  const learningOpp = await getLO(id, context.req);
  const redirect = getRedirectIfUrlMismatch(context, learningOpp);
  if (redirect) {
    return redirect;
  }
  const getUserAdmissionFiles = async () => {
    if (learningOpp.userAdmissionData) {
      return await getAdmissionFiles(
        learningOpp.userAdmissionData.admissionId,
        context.req
      );
    }
    return [];
  };

  const [userAdmissionFiles, tree, answers, competencePassport, prerequisites] =
    await Promise.all([
      getUserAdmissionFiles(),
      getCompetenceTree(),
      getSSRMotivationScan(context),
      getCompetencePassportOrNull(context.req),
      checkPrerequisites([id], context.req),
    ]);

  const sortedLearners = getSortedLearners(learningOpp, context);
  const pageVariant = getLoPageVariant(learningOpp);
  const hasRelated = false;
  const anchorLinks = createAnchorLinksForPageVariant(
    pageVariant,
    LO_PAGE_SECTIONS_DEFAULT,
    LO_PAGE_SECTIONS_APPLIED,
    LO_PAGE_SECTIONS_ATTENDING,
    [
      !hasRelated && SECTIONS.relatedLOs,
      learningOpp.simplifiedAdmissionProcess === true &&
        SECTIONS.yourApplication,
      learningOpp.learningOutcomes.length === 0 && SECTIONS.valueAndProgress,
    ]
  );
  const loListItems: LearningOpportunityCardContainerProps[] = [];
  const castedQuery = context.query as LearningOpportunityPageQueryParams;
  const showInitialHelpText = castedQuery.showInitialHelpText === "1";
  const authStatus = getAuthStatus(context.req);
  return {
    props: {
      learningOpp,
      userAdmissionFiles,
      sortedLearners,
      anchorLinks,
      pageVariant,
      loListItems,
      showInitialHelpText,
      tree,
      answers,
      isSignedIn: authStatus.isAuthenticated,
      competencePassport: competencePassport,
      prerequisites,
    },
  };
};

export const getServerSideProps = withErrorReportingAndAuthorization(
  getServerSidePropsImpl
);
const LearningOpportunityPage: NextPage<PageProps> = (props) => {
  const {
    learningOpp,
    sortedLearners,
    userAdmissionFiles,
    anchorLinks,
    pageVariant,
    loListItems,
    showInitialHelpText,
    tree,
    answers,
    isSignedIn,
    competencePassport,
  } = props;
  const certificateUrl = buildCertificateDownloadUrl(learningOpp.id);
  const statusLabel = getStatusLabelPropsFromStatus(
    learningOpp.status,
    learningOpp.userStatus
  );
  const { FixedSystemMessageList, addSystemMessage, hasSystemMessages } =
    useSystemMessages();
  const { logException } = useLogging();
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const {
    admissionResponseType,
    isSuccessDialogOpen,
    showSuccessDialogAndRedirect,
  } = useAdmissionResponseSuccess();
  async function handleSimplifiedProcessJoin() {
    setShowLoadingOverlay(true);
    try {
      await joinLearningOppWithSimplifiedProcess(learningOpp);
      const url = buildLearningOpportunityUrl(
        learningOpp.id,
        learningOpp.title,
        {
          showInitialHelpText: "1",
        }
      );
      setShowLoadingOverlay(false);
      showSuccessDialogAndRedirect(
        AdmissionResponseType.SimplifiedProcessJoin,
        url
      );
    } catch (err) {
      logException(err, {
        message:
          "Join learning opportunity with simplified admission process error.",
        learningOppId: learningOpp.id,
      });
      addSystemMessage({
        text: "An error occurred when attempting to join this learning opportunity. Please try again later.",
        props: {
          kind: "error",
        },
      });
      setShowLoadingOverlay(false);
    }
  }

  const getLearningOppDetails = () => {
    switch (learningOpp.type) {
      case LearningOpportunityType.MicroModule:
        return {
          typeLabel: "micro-module",
          roundedType: "full" as Rounded,
          descriptionText: "Description",
        };
      case LearningOpportunityType.Challenge:
        return {
          typeLabel: "challenge",
          roundedType: "3" as Rounded,
          descriptionText: "Our Challenge",
        };
      default:
        return {
          typeLabel: "",
          roundedType: "full" as Rounded,
          descriptionText: "Description",
        };
    }
  };
  return (
    <>
      <LearningOppHead learningOpp={learningOpp} />
      {hasSystemMessages && <FixedSystemMessageList />}
      {showLoadingOverlay && (
        <SpinnerOverlay
          loadingText="Joining learning opportunity..."
          isTransparent
        />
      )}
      <AdmissionResponseSuccessDialog
        open={isSuccessDialogOpen}
        admissionResponseType={admissionResponseType}
      />
      <ObjectHeader
        type={learningOpp.type}
        typeLabel={getLearningOppDetails().typeLabel}
        title={learningOpp.title}
        subTitle={learningOpp.subTitle}
        certificateUrl={certificateUrl}
        generateDigitalCredentials={learningOpp.generateDigitalCredentials}
        anchorLinksProps={{
          anchorLinks: anchorLinks,
          onRenderRight: (isSticking) => (
            <LearningOpportunityApplyButton
              isSticking={isSticking}
              learningOpportunity={learningOpp}
              onSimplifiedProcessWithoutDocumentsJoinCallback={() =>
                handleSimplifiedProcessJoin()
              }
              prereq={props.prerequisites[0]}
            />
          ),
        }}
        image={
          learningOpp.imageUri ? (
            <EciuImage
              src={learningOpp.imageUri}
              rounded={getLearningOppDetails().roundedType}
              alt={"Image for learning opportunity " + learningOpp.title}
            />
          ) : null
        }
        labels={statusLabel ? [statusLabel] : undefined}
      />

      {(() => {
        const firstSectionClassname = "section-after-object-header";

        switch (pageVariant) {
          case "normal":
            return (
              <LearningOppMetaAndInfoColumnSection
                id={SECTIONS.description.id}
                learningOpp={learningOpp}
                heading={getLearningOppDetails().descriptionText}
                color="light-blue"
                className="flex-grow"
                containerClassName={firstSectionClassname}
              />
            );
          case "admission-sent":
            return (
              <>
                <AdmissionProgressSection
                  id={SECTIONS.yourApplication.id}
                  color="white"
                  className="flex-grow"
                  containerClassName={firstSectionClassname}
                  learningOpp={learningOpp}
                  userAdmissionFiles={userAdmissionFiles ?? []}
                />
                <LearningOppMetaAndInfoColumnSection
                  id={SECTIONS.description.id}
                  learningOpp={learningOpp}
                  heading={getLearningOppDetails().descriptionText}
                  color="light-blue"
                  className="flex-grow"
                />
              </>
            );
          case "attending":
            return (
              <LearningOppAttendingSection
                id={SECTIONS.yourTeam.id}
                color="light-blue"
                learningOpp={learningOpp}
                teamchers={learningOpp.teamchers}
                learners={sortedLearners}
                containerClassName={firstSectionClassname}
                showInitialHelpText={showInitialHelpText}
              />
            );
        }
      })()}
      <LearningOppInformationTextTabSection
        learningOpp={learningOpp}
        highlightParticipantInfoTab={pageVariant === "attending"}
      />
      <LearningOutcomeSections
        learningOutcomes={learningOpp.learningOutcomes}
        tree={tree}
        answers={answers}
        isSignedIn={isSignedIn}
        competencePassport={competencePassport}
        valueAndProgressSectionId={SECTIONS.valueAndProgress.id}
      />
      {pageVariant === "attending" && (
        <>
          <LearningOppAttendingMetaSection
            id={SECTIONS.description.id}
            color="light-blue"
            heading={staticData.overviewHeading}
            learningOpp={learningOpp}
          />
          {!learningOpp.simplifiedAdmissionProcess && (
            <AdmissionProgressSection
              id={SECTIONS.yourApplication.id}
              learningOpp={learningOpp}
              userAdmissionFiles={userAdmissionFiles}
              color="white"
            />
          )}
        </>
      )}

      <LearningOppProviderSection
        id={SECTIONS.provider.id}
        learningOpp={learningOpp}
      />
      <LearningOpportunitySection
        id={SECTIONS.relatedLOs.id}
        color="light-gray"
        data={loListItems}
      />
    </>
  );
};

export default LearningOpportunityPage;
